import {
  Download, FileUp, FolderOpen, Grid3X3, Hand, HelpCircle, ImageDown, Maximize2, MousePointer2,
  Play, Printer, Redo2, RotateCcw, Ruler, SlidersHorizontal, Sparkles, Trash2, Undo2, X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AnnotationDrawer } from "@/src/components/AnnotationDrawer";
import { MapCanvas, type MapCanvasHandle } from "@/src/components/Canvas/MapCanvas";
import { ExportModal } from "@/src/components/ExportModal";
import { PwaRegister } from "@/src/components/PwaRegister";
import { MapLibrary } from "@/src/components/MapLibrary";
import { PatchDrawer } from "@/src/components/PatchDrawer";
import { RoomKeyPanel } from "@/src/components/RoomKeyPanel";
import { SettingsPanel } from "@/src/components/SettingsPanel";
import { StampDrawer } from "@/src/components/StampDrawer";
import { TerrainDrawer } from "@/src/components/TerrainDrawer";
import { Toolbar } from "@/src/components/Toolbar/Toolbar";
import {
  downloadPngBlob,
  exportDocumentJson,
  parseMapDocument,
} from "@/src/core/export/jsonExporter";
import { directionFor, t as translateStatic, useLanguage, useT } from "@/src/i18n";
import { startAutosave } from "@/src/core/persistence/autosave";
import {
  bootstrapSession,
  createAndOpenBlankMap,
  importDocumentAsMap,
} from "@/src/core/persistence/session";
import { floorForMode, hexesForMode, objectsForMode } from "@/src/core/mapMode";
import { useMapStore } from "@/src/store/useMapStore";
import type { TranslationKey } from "@/src/i18n";
import type { ActiveTool } from "@/src/types/map";

const actionClass = "inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] px-3 text-xs font-medium text-stone-300 transition hover:border-white/20 hover:bg-white/[0.08] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d85b4f] disabled:cursor-not-allowed disabled:opacity-35";

const toolHintKeys: Record<ActiveTool, TranslationKey> = {
  select: "hint.select",
  pan: "hint.pan",
  room: "hint.room",
  ellipse: "hint.ellipse",
  hexagon: "hint.hexagon",
  corridor: "hint.corridor",
  polygon: "hint.polygon",
  erase: "hint.erase",
  stamp: "hint.stamp",
  terrain: "hint.terrain",
  patch: "hint.patch",
  label: "hint.label",
  measure: "hint.measure",
};

/**
 * The box a tool drawer lives in. Giving it both a top and a bottom inset makes
 * it a definite-height box, which is what lets the drawer inside cap itself with
 * max-h-full and scroll internally instead of running off the screen (D-022).
 * Bottom-anchored on phones so it still sits above the mobile toolbar,
 * top-anchored from md up, which is where it hangs beside the tool rail.
 */
const TOOL_DRAWER_SLOT =
  "absolute bottom-20 start-3 top-3 z-20 flex flex-col justify-end " +
  "md:bottom-3 md:start-[88px] md:justify-start";

/**
 * The box for the panels that open from the end edge (appearance, map library,
 * controls). Same reasoning; these hang below the header buttons.
 */
const END_PANEL_SLOT = "absolute bottom-3 end-3 top-16 z-40 flex flex-col";

export function DungeonBuilder() {
  const canvasRef = useRef<MapCanvasHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saved" | "error">("idle");
  const [toast, setToast] = useState<string | null>(null);
  const [presentMode, setPresentMode] = useState(false);

  const tool = useMapStore((state) => state.tool);
  const setTool = useMapStore((state) => state.setTool);
  const selectedTerrain = useMapStore((state) => state.selectedTerrain);
  const selectedStampAsset = useMapStore((state) => state.selectedStampAsset);
  const cycleWallType = useMapStore((state) => state.cycleWallType);
  const rotateFloorPlacement = useMapStore((state) => state.rotateFloorPlacement);
  const setSelectedTerrain = useMapStore((state) => state.setSelectedTerrain);
  const meta = useMapStore((state) => state.meta);
  const setTitle = useMapStore((state) => state.setTitle);
  const geometry = useMapStore((state) => state.floorPolygons);
  const hexCells = useMapStore((state) => state.hexCells);
  const objects = useMapStore((state) => state.objects);
  const selectedObjectId = useMapStore((state) => state.selectedObjectId);
  const viewport = useMapStore((state) => state.viewport);
  const past = useMapStore((state) => state.past);
  const future = useMapStore((state) => state.future);
  const undo = useMapStore((state) => state.undo);
  const redo = useMapStore((state) => state.redo);
  const hydrated = useMapStore((state) => state.hydrated);
  const language = useLanguage();
  const t = useT();
  const getDocument = useMapStore((state) => state.getDocument);
  const deleteSelectedObject = useMapStore((state) => state.deleteSelectedObject);
  const duplicateSelectedObject = useMapStore((state) => state.duplicateSelectedObject);
  const rotateSelectedObject = useMapStore((state) => state.rotateSelectedObject);
  const resizeSelectedObject = useMapStore((state) => state.resizeSelectedObject);
  const nudgeSelectedObject = useMapStore((state) => state.nudgeSelectedObject);
  const toggleSelectedObjectLock = useMapStore((state) => state.toggleSelectedObjectLock);
  const selectObject = useMapStore((state) => state.selectObject);
  const roomKeyPanelObjectId = useMapStore((state) => state.roomKeyPanelObjectId);
  const closeRoomKeyPanel = useMapStore((state) => state.closeRoomKeyPanel);
  const activeGeometry = floorForMode(geometry, meta.gridType);
  const activeHexCells = hexesForMode(hexCells, meta.gridType);
  const activeObjects = objectsForMode(objects, meta.gridType);

  // Keep the document in step with the chosen language. Layout direction is a
  // document-level concern, so it is set here rather than in index.html, which
  // only carries the pre-hydration default.
  useEffect(() => {
    const root = document.documentElement;
    root.lang = language;
    root.dir = directionFor(language);
  }, [language]);

  // The room key panel shares the end-side slot with appearance, library and
  // controls, so opening it has to close them. It opens from a double-click on
  // the canvas, which reaches here only through the store — so this subscribes
  // to that store event rather than reacting to a rendered value. The reverse
  // direction is handled on the three buttons themselves.
  useEffect(() => useMapStore.subscribe((state, previous) => {
    if (state.roomKeyPanelObjectId && state.roomKeyPanelObjectId !== previous.roomKeyPanelObjectId) {
      setSettingsOpen(false);
      setLibraryOpen(false);
      setHelpOpen(false);
    }
  }), []);

  // Load the last-opened map, then mirror every change into IndexedDB.
  useEffect(() => {
    let stopAutosave: (() => void) | undefined;
    let disposed = false;
    void bootstrapSession()
      // translateStatic, not t: this effect must run exactly once, so it cannot
      // take a reactive dependency on the language.
      .catch(() => setToast(translateStatic("toast.storageFailed")))
      .finally(() => {
        // The effect can be torn down before bootstrap settles (StrictMode does
        // exactly this), so re-check rather than starting an unstoppable saver.
        if (disposed) return;
        stopAutosave = startAutosave({
          onSaved: () => setSaveState("saved"),
          onError: () => setSaveState("error"),
        });
      });
    return () => {
      disposed = true;
      stopAutosave?.();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const element = event.target as HTMLElement | null;
      const editing = element?.tagName === "INPUT" || element?.tagName === "TEXTAREA" || element?.isContentEditable;
      if (editing) return;
      if (presentMode) {
        if (event.key === "Escape") {
          event.preventDefault();
          selectObject(null);
          setPresentMode(false);
          return;
        }
        const runShortcuts: Partial<Record<string, ActiveTool>> = {
          v: "select", h: "pan", p: "pan", m: "measure",
        };
        const runTool = runShortcuts[event.key.toLowerCase()];
        if (runTool) {
          event.preventDefault();
          setTool(runTool);
        } else if (event.key === "0") {
          event.preventDefault();
          canvasRef.current?.fitMap();
        }
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "d" && selectedObjectId) {
        event.preventDefault();
        duplicateSelectedObject();
        return;
      }
      if (selectedObjectId && event.key === "Escape") {
        event.preventDefault();
        selectObject(null);
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedObjectId && tool === "select") {
        event.preventDefault();
        deleteSelectedObject();
        return;
      }
      if (selectedObjectId && (event.code === "BracketLeft" || event.code === "BracketRight")) {
        event.preventDefault();
        rotateSelectedObject((event.code === "BracketLeft" ? -1 : 1) * (event.shiftKey ? 90 : 15));
        return;
      }
      if (selectedObjectId && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        const step = meta.gridSizeWorld * (event.shiftKey ? 1 : 0.5);
        nudgeSelectedObject(
          event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0,
          event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0,
        );
        return;
      }
      if (selectedObjectId && (event.key === "+" || event.key === "=" || event.key === "-")) {
        event.preventDefault();
        resizeSelectedObject(event.key === "-" ? 0.8 : 1.25);
        return;
      }
      if (selectedObjectId && event.shiftKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        toggleSelectedObjectLock();
        return;
      }
      const key = event.key.toLowerCase();
      // R rotates the vertical-movement family: the pending placement while the
      // Objects tool holds one, or the current selection just after placing.
      // Everywhere else R keeps its original meaning, the Room tool.
      if (key === "r" && !event.ctrlKey && !event.metaKey) {
        const placingFloorObject = tool === "stamp" && selectedStampAsset === "stairs";
        const selectedIsFloorObject = Boolean(selectedObjectId)
          && objects.find((item) => item.id === selectedObjectId)?.assetId === "stairs";
        if (placingFloorObject || selectedIsFloorObject) {
          event.preventDefault();
          const step = event.shiftKey ? -45 : 45;
          if (placingFloorObject) rotateFloorPlacement(step);
          if (selectedIsFloorObject) rotateSelectedObject(step);
          return;
        }
      }
      // Cycle the door & passage family while the Objects tool holds a wall object.
      if (key === "d" && !event.ctrlKey && !event.metaKey && tool === "stamp" && selectedStampAsset === "door") {
        event.preventDefault();
        cycleWallType(event.shiftKey ? -1 : 1);
        return;
      }
      if (meta.gridType === "hex" && key === "e") {
        event.preventDefault();
        setSelectedTerrain("erase");
        setTool("terrain");
        return;
      }
      // P is the patch tool on the dungeon canvas; it stays Pan on the hex canvas,
      // where there are no patches.
      const commonShortcuts: Partial<Record<string, ActiveTool>> = {
        v: "select", h: "pan", s: "stamp", l: "label", m: "measure",
        ...(meta.gridType === "hex" ? { p: "pan" as ActiveTool } : {}),
      };
      const dungeonShortcuts: Partial<Record<string, ActiveTool>> = {
        r: "room", o: "ellipse", x: "hexagon", c: "corridor", g: "polygon", e: "erase", p: "patch",
      };
      const hexShortcuts: Partial<Record<string, ActiveTool>> = { t: "terrain" };
      const selectedTool = commonShortcuts[key] ?? (meta.gridType === "square" ? dungeonShortcuts[key] : hexShortcuts[key]);
      if (selectedTool) setTool(selectedTool);
      if (event.key === "0") canvasRef.current?.fitMap();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cycleWallType, objects, rotateFloorPlacement, deleteSelectedObject, duplicateSelectedObject, meta.gridSizeWorld, meta.gridType, nudgeSelectedObject, presentMode, redo, resizeSelectedObject, rotateSelectedObject, selectObject, selectedObjectId, selectedStampAsset, setSelectedTerrain, setTool, toggleSelectedObjectLock, tool, undo]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleImport = async (file: File) => {
    try {
      const text = await file.text();
      const document = parseMapDocument(JSON.parse(text) as unknown);
      await importDocumentAsMap(document);
      window.setTimeout(() => canvasRef.current?.fitMap(), 0);
      setToast(t("toast.imported", { title: document.meta.title }));
    } catch (error) {
      setToast(error instanceof Error ? error.message : t("toast.importFailed"));
    }
  };

  const handlePngExport = async () => {
    try {
      const blob = await canvasRef.current?.exportPng();
      if (!blob) throw new Error(t("toast.pngNotReady"));
      downloadPngBlob(blob, meta.title);
      setToast(t("toast.pngExported"));
    } catch (error) {
      setToast(error instanceof Error ? error.message : t("toast.pngFailed"));
    }
  };

  const enterPresentMode = () => {
    selectObject(null);
    setPresentMode(true);
    setSettingsOpen(false);
    setLibraryOpen(false);
    setHelpOpen(false);
    setTool("select");
    setToast(t("toast.runMode"));
  };

  return (
    <main className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-[#11110f] text-stone-100">
      <PwaRegister />
      {!presentMode && <header className="relative z-30 flex h-16 shrink-0 items-center border-b border-white/8 bg-[#181714] px-3 shadow-xl shadow-black/20 md:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#a72f29] text-sm font-black tracking-[-0.08em] text-white shadow-lg shadow-red-950/30">
            DB<span className="absolute -end-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-[#181714] bg-[#e9a85b]" />
          </div>
          <div className="hidden leading-none sm:block">
            <p className="font-serif text-[15px] font-semibold tracking-wide text-stone-100">{t("app.name")}</p>
            <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.22em] text-stone-500">{t("app.tagline")}</p>
          </div>
        </div>

        <div className="mx-3 h-6 w-px shrink-0 bg-white/10 md:mx-5" />
        <label className="group flex min-w-0 flex-1 items-center gap-2">
          <span className="sr-only">{t("app.mapTitleLabel")}</span>
          <input value={meta.title} onChange={(event) => setTitle(event.target.value)} className="min-w-0 max-w-72 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1.5 font-serif text-sm font-semibold text-stone-200 outline-none transition hover:border-white/8 hover:bg-white/[0.025] focus:border-white/15 focus:bg-white/[0.05]" />
          <span className="hidden items-center gap-1.5 text-[10px] text-stone-600 xl:flex"><span className={`h-1.5 w-1.5 rounded-full ${saveState === "error" ? "bg-red-500" : saveState === "saved" ? "bg-emerald-600" : "bg-stone-600"}`} />{saveState === "error" ? t("app.save.error") : saveState === "saved" ? t("app.save.saved") : hydrated ? t("app.save.ready") : t("app.save.loading")}</span>
        </label>

        <div className="flex shrink-0 items-center gap-1.5">
          <button type="button" className={actionClass} disabled={past.length === 0} onClick={undo} aria-label={t("header.undo")} title={t("header.undoTitle")}><Undo2 size={15} /></button>
          <button type="button" className={actionClass} disabled={future.length === 0} onClick={redo} aria-label={t("header.redo")} title={t("header.redoTitle")}><Redo2 size={15} /></button>
          <div className="mx-1 hidden h-6 w-px bg-white/10 md:block" />
          <button type="button" className={actionClass} onClick={() => { setLibraryOpen((open) => !open); setSettingsOpen(false); setHelpOpen(false); closeRoomKeyPanel(); }} aria-expanded={libraryOpen} aria-label={t("header.libraryLabel")} title={t("header.libraryLabel")}><FolderOpen size={15} /><span className="hidden lg:inline">{t("header.library")}</span></button>
          <button type="button" className={`${actionClass} hidden lg:inline-flex`} onClick={() => fileInputRef.current?.click()}><FileUp size={15} /> {t("header.import")}</button>
          <button type="button" className={`${actionClass} hidden xl:inline-flex`} onClick={() => exportDocumentJson(getDocument())}><Download size={15} /> {t("header.json")}</button>
          <button type="button" className={`${actionClass} hidden md:inline-flex`} onClick={handlePngExport}><ImageDown size={15} /> {t("header.png")}</button>
          <button type="button" className={actionClass} onClick={enterPresentMode} aria-label={t("header.runMap")} title={t("header.runMap")}><Play size={15} /><span className="hidden lg:inline">{t("header.runMap")}</span></button>
          <button type="button" className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#ad342e] px-3 text-xs font-semibold text-white shadow-lg shadow-red-950/25 transition hover:bg-[#bf3d35]" onClick={() => setExportOpen(true)}><Printer size={15} /><span className="hidden sm:inline">{t("header.printPdf")}</span></button>
        </div>

        <input ref={fileInputRef} className="hidden" type="file" accept="application/json,.json" onChange={(event) => { const file = event.target.files?.[0]; if (file) void handleImport(file); event.target.value = ""; }} />
      </header>}

      <section className="relative min-h-0 flex-1">
        <MapCanvas ref={canvasRef} runMode={presentMode} />

        {!presentMode && <div className="absolute start-3 top-3 z-20 hidden md:block"><Toolbar /></div>}
        {!presentMode && tool === "stamp" && <div className={TOOL_DRAWER_SLOT}><StampDrawer /></div>}
        {!presentMode && tool === "label" && <div className={TOOL_DRAWER_SLOT}><AnnotationDrawer /></div>}
        {!presentMode && tool === "terrain" && <div className={TOOL_DRAWER_SLOT}><TerrainDrawer /></div>}
        {!presentMode && tool === "patch" && <div className={TOOL_DRAWER_SLOT}><PatchDrawer /></div>}
        {!presentMode && <div className="absolute end-3 top-3 z-20 flex items-center gap-2">
          <button type="button" onClick={() => canvasRef.current?.fitMap()} className="grid h-10 w-10 place-items-center rounded-xl border border-black/15 bg-[#f4ecdb]/95 text-[#41372e] shadow-lg shadow-black/10 backdrop-blur transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a72f29]" aria-label={t("header.fitMap")} title={t("header.fitMapTitle")}><Maximize2 size={17} /></button>
          <button type="button" onClick={() => { setSettingsOpen((open) => !open); setHelpOpen(false); closeRoomKeyPanel(); }} className="grid h-10 w-10 place-items-center rounded-xl border border-black/15 bg-[#f4ecdb]/95 text-[#41372e] shadow-lg shadow-black/10 backdrop-blur transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a72f29]" aria-label={t("header.appearance")} aria-expanded={settingsOpen}><SlidersHorizontal size={17} /></button>
          <button type="button" onClick={() => { setHelpOpen((open) => !open); setSettingsOpen(false); closeRoomKeyPanel(); }} className="grid h-10 w-10 place-items-center rounded-xl border border-black/15 bg-[#f4ecdb]/95 text-[#41372e] shadow-lg shadow-black/10 backdrop-blur transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#a72f29]" aria-label={t("header.showControls")} aria-expanded={helpOpen}><HelpCircle size={18} /></button>
        </div>}

        {!presentMode && settingsOpen && <div className={END_PANEL_SLOT}><SettingsPanel onClose={() => setSettingsOpen(false)} /></div>}
        {!presentMode && libraryOpen && <div className={END_PANEL_SLOT}><MapLibrary onClose={() => setLibraryOpen(false)} onMessage={setToast} /></div>}
        {/* Keyed on the room so switching rooms remounts the panel: the draft
            state reseeds and the outstanding-edit flush runs on the way out. */}
        {!presentMode && roomKeyPanelObjectId && <div className={END_PANEL_SLOT}><RoomKeyPanel key={roomKeyPanelObjectId} objectId={roomKeyPanelObjectId} onClose={closeRoomKeyPanel} /></div>}

        {!presentMode && helpOpen && (
          <aside className="absolute bottom-3 end-3 top-16 z-40 flex max-h-[calc(100%-4.75rem)] w-[min(340px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#1b1a17]/97 shadow-2xl shadow-black/35 backdrop-blur">
            <div className="flex shrink-0 items-start justify-between border-b border-white/8 p-4">
              <div><p className="flex items-center gap-2 text-sm font-semibold text-stone-100"><Sparkles size={15} className="text-[#dd665a]" /> {t("help.title")}</p><p className="mt-1 text-xs text-stone-500">{t("help.subtitle")}</p></div>
              <button type="button" onClick={() => setHelpOpen(false)} className="rounded-md p-1 text-stone-500 hover:bg-white/5 hover:text-white" aria-label={t("help.close")}><X size={16} /></button>
            </div>
            <div className="grid min-h-0 flex-1 gap-2 overflow-y-auto p-4 text-xs leading-5 text-stone-400">
              <p><strong className="text-stone-200">{t("help.roomsLabel")}</strong> {t("help.roomsBody")}</p>
              <p><strong className="text-stone-200">{t("help.modesLabel")}</strong> {t("help.modesBody")}</p>
              <p><strong className="text-stone-200">{t("help.hexLabel")}</strong> {t("help.hexBody")}</p>
              <p><strong className="text-stone-200">{t("help.pathLabel")}</strong> {t("help.pathBody")}</p>
              <p><strong className="text-stone-200">{t("help.freeLabel")}</strong> {t("help.freeBody")}</p>
              <p><strong className="text-stone-200">{t("help.stampsLabel")}</strong> {t("help.stampsBody")}</p>
              <p><strong className="text-stone-200">{t("help.objectsLabel")}</strong> {t("help.objectsBody")}</p>
              <p><strong className="text-stone-200">{t("help.labelsLabel")}</strong> {t("help.labelsBody")}</p>
              <p><strong className="text-stone-200">{t("help.roomKeysLabel")}</strong> {t("help.roomKeysBody")}</p>
              <p><strong className="text-stone-200">{t("help.runLabel")}</strong> {t("help.runBody")}</p>
              <p><strong className="text-stone-200">{t("help.moveLabel")}</strong> {t("help.moveBody")}</p>
              <div className="grid grid-cols-2 gap-2 border-t border-white/8 pt-3 text-[10px] uppercase tracking-wider text-stone-600">
                <span>{t("help.keyWheel")}</span><span>{t("help.keyFit")}</span><span>{t("help.keyRoom")}</span><span>{t("help.keyTerrain")}</span><span>{t("help.keyLabel")}</span><span>{t("help.keyMeasure")}</span><span>{t("help.keyObjects")}</span><span>{t("help.keyUndo")}</span>
              </div>
            </div>
          </aside>
        )}

        {activeGeometry.length === 0 && activeHexCells.length === 0 && activeObjects.length === 0 && (
          <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center p-6">
            <div className="max-w-sm rounded-3xl border border-[#4d4033]/20 bg-[#f5eddd]/90 px-7 py-6 text-center text-[#44382e] shadow-xl shadow-black/10 backdrop-blur-sm">
              <Grid3X3 size={24} className="mx-auto mb-3 text-[#a72f29]" /><h2 className="font-serif text-xl font-bold">{meta.gridType === "hex" ? t("empty.hexTitle") : t("empty.dungeonTitle")}</h2><p className="mt-2 text-sm leading-6 text-[#746454]">{meta.gridType === "hex" ? t("empty.hexBody") : t("empty.dungeonBody")}</p>
            </div>
          </div>
        )}

        {!presentMode && <div className="absolute bottom-3 start-3 z-30 md:hidden"><Toolbar /></div>}
        {!presentMode && <div className="absolute bottom-3 end-3 z-20 hidden items-center gap-1.5 rounded-xl border border-black/15 bg-[#f4ecdb]/92 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#6c5b4c] shadow-lg shadow-black/10 backdrop-blur sm:flex">
          <Grid3X3 size={13} />{meta.gridType === "hex" ? t("status.hexcrawl") : t("status.dungeon")} · {meta.gridSizeWorld}px<span className="mx-1 h-3 w-px bg-black/15" />{Math.round(viewport.zoom * 100)}%<span className="mx-1 h-3 w-px bg-black/15" />{meta.gridType === "hex" ? t("status.hexes", { count: activeHexCells.length }) : t("status.regions", { count: activeGeometry.length })}<span className="mx-1 h-3 w-px bg-black/15" />{t("status.objects", { count: activeObjects.length })}
        </div>}
        {!presentMode && <div className="absolute bottom-3 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-2 rounded-xl border border-black/10 bg-[#f4ecdb]/82 px-3 py-2 text-[10px] font-medium text-[#736352] shadow-md backdrop-blur lg:flex"><span className="text-[#a72f29]">{t(`tool.${tool}`)}</span><span className="h-1 w-1 rounded-full bg-[#887665]" />{selectedObjectId && tool === "select" ? t("hint.selectedObject") : tool === "terrain" && selectedTerrain === "erase" ? t("hint.eraseBrush") : t(toolHintKeys[tool])}</div>}

        {presentMode && (
          <>
            <div className="pointer-events-none absolute start-3 top-3 z-30 max-w-[45vw] rounded-xl border border-black/10 bg-[#f7efdf]/92 px-3 py-2 text-[#3d3229] shadow-lg backdrop-blur">
              <p className="truncate font-serif text-sm font-bold">{meta.title}</p>
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#9c3b33]">{t("run.badge")}</p>
            </div>
            <div className="absolute end-3 top-3 z-30 flex items-center gap-1 rounded-2xl border border-white/10 bg-[#1c1b18]/95 p-1.5 shadow-2xl shadow-black/25 backdrop-blur" role="toolbar" aria-label={t("run.controls")}>
              <button type="button" onClick={() => setTool("select")} className={`grid h-10 w-10 place-items-center rounded-xl transition ${tool === "select" ? "bg-[#b4362f] text-white" : "text-stone-400 hover:bg-white/10 hover:text-white"}`} aria-label={t("run.moveTokens")} title={t("run.moveTokens")}><MousePointer2 size={17} /></button>
              <button type="button" onClick={() => setTool("pan")} className={`grid h-10 w-10 place-items-center rounded-xl transition ${tool === "pan" ? "bg-[#b4362f] text-white" : "text-stone-400 hover:bg-white/10 hover:text-white"}`} aria-label={t("run.panMap")} title={t("run.panMap")}><Hand size={17} /></button>
              <button type="button" onClick={() => setTool("measure")} className={`grid h-10 w-10 place-items-center rounded-xl transition ${tool === "measure" ? "bg-[#b4362f] text-white" : "text-stone-400 hover:bg-white/10 hover:text-white"}`} aria-label={t("run.measure")} title={t("run.measure")}><Ruler size={17} /></button>
              <button type="button" onClick={() => canvasRef.current?.fitMap()} className="grid h-10 w-10 place-items-center rounded-xl text-stone-400 transition hover:bg-white/10 hover:text-white" aria-label={t("run.fitMap")} title={t("run.fitMap")}><Maximize2 size={17} /></button>
              <span className="mx-0.5 h-6 w-px bg-white/10" />
              <button type="button" onClick={() => setPresentMode(false)} className="flex h-10 items-center gap-1.5 rounded-xl px-2.5 text-[10px] font-semibold text-stone-300 transition hover:bg-white/10 hover:text-white" aria-label={t("run.exitLabel")}><X size={15} /><span className="hidden sm:inline">{t("run.exit")}</span></button>
            </div>
          </>
        )}
      </section>

      {!presentMode && <footer className="relative z-30 flex h-9 shrink-0 items-center justify-between border-t border-white/8 bg-[#181714] px-3 text-[10px] text-stone-600 md:px-5">
        <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />{t("footer.installable")}</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 transition hover:text-stone-300 lg:hidden"><FileUp size={12} /> {t("header.import")}</button>
          <button type="button" onClick={() => exportDocumentJson(getDocument())} className="hidden items-center gap-1.5 transition hover:text-stone-300 sm:flex xl:hidden"><Download size={12} /> {t("header.json")}</button>
          <button type="button" onClick={handlePngExport} className="hidden items-center gap-1.5 transition hover:text-stone-300 sm:flex md:hidden"><ImageDown size={12} /> {t("header.png")}</button>
          <button type="button" onClick={() => { void createAndOpenBlankMap().then((record) => setToast(t("library.created", { title: record.document.meta.title }))).catch(() => setToast(t("toast.newMapFailed"))); }} className="flex items-center gap-1.5 transition hover:text-stone-300"><Trash2 size={12} /> {t("footer.newMap")}</button>
          <span className="hidden items-center gap-1.5 sm:flex"><RotateCcw size={12} /> {t("footer.history", { count: past.length })}</span>
        </div>
      </footer>}

      <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} onMessage={setToast} />
      {toast && <div role="status" className="fixed bottom-14 left-1/2 z-[80] max-w-[calc(100vw-24px)] -translate-x-1/2 rounded-xl border border-white/10 bg-[#201e1a] px-4 py-3 text-center text-xs font-medium text-stone-200 shadow-2xl shadow-black/40">{toast}</div>}
    </main>
  );
}
